// Browser-compatible EventEmitter implementation
class BrowserEventEmitter {
  private events: Map<string, Array<(...args: any[]) => void>> = new Map();
  private maxListeners: number = 10;

  setMaxListeners(n: number): this {
    this.maxListeners = n;
    return this;
  }

  on(event: string, listener: (...args: any[]) => void): this {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(listener);
    return this;
  }

  emit(event: string, ...args: any[]): boolean {
    const listeners = this.events.get(event);
    if (!listeners || listeners.length === 0) {
      return false;
    }
    
    listeners.forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error('EventEmitter listener error:', error);
      }
    });
    return true;
  }

  removeListener(event: string, listener: (...args: any[]) => void): this {
    const listeners = this.events.get(event);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
    return this;
  }

  removeAllListeners(event?: string): this {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
    return this;
  }
}

// import { createEventLogRepo, type EventLogEntry } from './EventLogRepo';

export interface EventPayload {
  source: string;
  timestamp: number;
  userId?: string;
  tenantId?: string;
  data: any;
  metadata?: Record<string, any>;
  eventId?: string; // For idempotency
}

export interface EventSubscription {
  id: string;
  eventType: string;
  callback: (payload: EventPayload) => void;
  filters?: Record<string, any>;
}

export class EventBus extends BrowserEventEmitter {
  private static instance: EventBus;
  private subscriptions: Map<string, EventSubscription[]> = new Map();
  private eventHistory: EventPayload[] = [];
  private maxHistorySize: number = 1000;
  private eventLogRepo: any;
  private deadLetterQueue: Map<string, EventPayload> = new Map();
  private dlqMaxSize: number = 1000;
  private demoMode: boolean = true;

  private constructor() {
    super();
    this.setMaxListeners(100);
  }

  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  // Initialize event log repository (browser compatible)
  private async initializeEventLog(): Promise<void> {
    if (this.demoMode) {
      // In demo mode, use memory-only storage
      this.eventLogRepo = {
        storeEvent: async (event: EventPayload) => {
          console.log('[EventBus] Demo mode: storing event in memory', event);
        },
        getEventById: async (id: string) => {
          return this.eventHistory.find(e => e.eventId === id);
        }
      };
    } else {
      try {
        // this.eventLogRepo = await createEventLogRepo();
        console.warn('[EventBus] EventLogRepo temporarily disabled to avoid circular import');
        this.demoMode = true;
        await this.initializeEventLog();
      } catch (error) {
        console.warn('[EventBus] Failed to initialize event log repository, using demo mode', error);
        this.demoMode = true;
        await this.initializeEventLog();
      }
    }
  }

  // Publish event to all subscribers
  async publish(eventType: string, payload: Omit<EventPayload, 'timestamp'>): Promise<void> {
    const fullPayload: EventPayload = {
      ...payload,
      timestamp: Date.now(),
      eventId: payload.eventId || `${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    // Initialize event log if not done yet
    if (!this.eventLogRepo) {
      await this.initializeEventLog();
    }

    // Store in persistent log
    if (this.eventLogRepo) {
      try {
        await this.eventLogRepo.storeEvent(fullPayload);
      } catch (error) {
        console.error('[EventBus] Failed to store event in log:', error);
      }
    }

    // Store in history
    this.eventHistory.push(fullPayload);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }

    // Emit to all listeners with error handling
    const subscribers = this.subscriptions.get(eventType) || [];
    const failedSubscribers: string[] = [];

    for (const subscription of subscribers) {
      try {
        if (this.matchesFilters(fullPayload, subscription.filters)) {
          subscription.callback(fullPayload);
        }
      } catch (error) {
        console.error(`[EventBus] Subscriber failed for event ${eventType}:`, error);
        failedSubscribers.push(subscription.id);
        
        // Add to dead letter queue
        this.addToDeadLetterQueue(eventType, fullPayload, error instanceof Error ? error.message : 'Unknown error');
      }
    }

    // Emit to general listeners
    this.emit(eventType, fullPayload);
    
    // Log for debugging
    console.log(`[EventBus] Published: ${eventType} (${subscribers.length} subscribers, ${failedSubscribers.length} failed)`, fullPayload);
  }

  // Add event to dead letter queue
  private addToDeadLetterQueue(eventType: string, payload: EventPayload, error: string): void {
    const dlqKey = `${eventType}_${payload.eventId}`;
    const dlqEntry = {
      ...payload,
      metadata: {
        ...payload.metadata,
        dlqError: error,
        dlqTimestamp: Date.now(),
        originalEventType: eventType
      }
    };

    this.deadLetterQueue.set(dlqKey, dlqEntry);

    // Limit DLQ size
    if (this.deadLetterQueue.size > this.dlqMaxSize) {
      const firstKey = this.deadLetterQueue.keys().next().value;
      this.deadLetterQueue.delete(firstKey);
    }

    console.log(`[EventBus] Added to DLQ: ${dlqKey}`, error);
  }

  // Subscribe to specific event types
  subscribe(eventType: string, callback: (payload: EventPayload) => void, filters?: Record<string, any>): string {
    const subscriptionId = `${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const subscription: EventSubscription = {
      id: subscriptionId,
      eventType,
      callback,
      filters
    };

    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, []);
    }
    
    this.subscriptions.get(eventType)!.push(subscription);
    
    // Add event listener
    this.on(eventType, (payload: EventPayload) => {
      if (this.matchesFilters(payload, filters)) {
        callback(payload);
      }
    });

    console.log(`[EventBus] Subscribed: ${eventType} (${subscriptionId})`);
    return subscriptionId;
  }

  // Unsubscribe from events
  unsubscribe(subscriptionId: string): boolean {
    for (const [eventType, subscriptions] of this.subscriptions.entries()) {
      const index = subscriptions.findIndex(sub => sub.id === subscriptionId);
      if (index !== -1) {
        const subscription = subscriptions[index];
        this.removeListener(eventType, subscription.callback);
        subscriptions.splice(index, 1);
        
        if (subscriptions.length === 0) {
          this.subscriptions.delete(eventType);
        }
        
        console.log(`[EventBus] Unsubscribed: ${eventType} (${subscriptionId})`);
        return true;
      }
    }
    return false;
  }

  // Check if payload matches filters
  private matchesFilters(payload: EventPayload, filters?: Record<string, any>): boolean {
    if (!filters) return true;
    
    for (const [key, value] of Object.entries(filters)) {
      if (payload.data[key] !== value && payload.metadata?.[key] !== value) {
        return false;
      }
    }
    return true;
  }

  // Get event history
  getEventHistory(eventType?: string, limit: number = 100): EventPayload[] {
    let history = this.eventHistory;
    
    if (eventType) {
      history = history.filter(event => event.eventType === eventType);
    }
    
    return history.slice(-limit);
  }

  // Clear event history
  clearEventHistory(): void {
    this.eventHistory = [];
  }

  // Get subscription statistics
  getSubscriptionStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const [eventType, subscriptions] of this.subscriptions.entries()) {
      stats[eventType] = subscriptions.length;
    }
    return stats;
  }

  // Dead Letter Queue methods
  getDeadLetterQueue(): EventPayload[] {
    return Array.from(this.deadLetterQueue.values());
  }

  async retryDeadLetterEvent(dlqKey: string): Promise<boolean> {
    const event = this.deadLetterQueue.get(dlqKey);
    if (!event) {
      return false;
    }

    try {
      const originalEventType = event.metadata?.originalEventType || 'unknown';
      await this.publish(originalEventType, event);
      this.deadLetterQueue.delete(dlqKey);
      console.log(`[EventBus] Successfully retried DLQ event: ${dlqKey}`);
      return true;
    } catch (error) {
      console.error(`[EventBus] Failed to retry DLQ event: ${dlqKey}`, error);
      return false;
    }
  }

  clearDeadLetterQueue(): number {
    const size = this.deadLetterQueue.size;
    this.deadLetterQueue.clear();
    console.log(`[EventBus] Cleared ${size} events from DLQ`);
    return size;
  }

  // Event replay functionality
  async replayEvents(eventIds: string[], targetWorkflow?: string): Promise<{ success: boolean; replayed: number; errors: string[] }> {
    if (!this.eventLogRepo) {
      return { success: false, replayed: 0, errors: ['Event log repository not available'] };
    }

    const results = { success: true, replayed: 0, errors: [] as string[] };

    for (const eventId of eventIds) {
      try {
        const event = await this.eventLogRepo.getEventById(eventId);
        if (event) {
          // Check for idempotency
          if (event.eventId && this.isEventProcessed(event.eventId)) {
            console.log(`[EventBus] Skipping already processed event: ${eventId}`);
            continue;
          }

          await this.publish(event.eventType, event);
          results.replayed++;
        } else {
          results.errors.push(`Event not found: ${eventId}`);
        }
      } catch (error) {
        const errorMsg = `Failed to replay event ${eventId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
        results.errors.push(errorMsg);
        results.success = false;
      }
    }

    console.log(`[EventBus] Replay completed: ${results.replayed} events replayed, ${results.errors.length} errors`);
    return results;
  }

  private isEventProcessed(eventId: string): boolean {
    // Check if event has already been processed
    // This is a simple implementation - in production, you'd want a more robust check
    return this.eventHistory.some(event => event.eventId === eventId);
  }

  // Get event log repository for external access
  getEventLogRepo(): any {
    return this.eventLogRepo;
  }

  // Set demo mode
  setDemoMode(demoMode: boolean): void {
    this.demoMode = demoMode;
    this.initializeEventLog();
  }
}

// Export singleton instance
export const eventBus = EventBus.getInstance();

// Predefined event types for common operations
export const EventTypes = {
  // Project Management Events
  PROJECT_CREATED: 'project:created',
  PROJECT_UPDATED: 'project:updated',
  PROJECT_COMPLETED: 'project:completed',
  TASK_ASSIGNED: 'task:assigned',
  MILESTONE_REACHED: 'milestone:reached',
  
  // Support Bridge Events
  TICKET_CREATED: 'ticket:created',
  TICKET_UPDATED: 'ticket:updated',
  TICKET_ESCALATED: 'ticket:escalated',
  TICKET_RESOLVED: 'ticket:resolved',
  ESCALATION_ALERT: 'escalation:alert',
  
  // Cross-System Events
  USER_ACTIVITY: 'user:activity',
  SYSTEM_HEALTH: 'system:health',
  DATA_SYNC: 'data:sync',
  WORKFLOW_TRIGGER: 'workflow:trigger',
  
  // Analytics Events
  METRICS_UPDATED: 'metrics:updated',
  REPORT_GENERATED: 'report:generated',
  ALERT_TRIGGERED: 'alert:triggered',
  
  // CRM Events
  CRM_CONTACT_SYNCED: 'crm:contact:synced',
  CRM_DEAL_SYNCED: 'crm:deal:synced',
  
  // Slack Events
  SLACK_NOTIFICATION_SENT: 'slack:notification:sent',
  SLACK_NOTIFICATION_FAILED: 'slack:notification:failed',
  
  // Google Drive Events
  DRIVE_DOCUMENT_UPLOADED: 'drive:document:uploaded',
  DRIVE_UPLOAD_FAILED: 'drive:upload:failed',
  DRIVE_FOLDER_CREATED: 'drive:folder:created'
} as const;

// Helper function to create standardized event payloads
export const createEventPayload = (
  source: string,
  data: any,
  userId?: string,
  tenantId?: string,
  metadata?: Record<string, any>
): Omit<EventPayload, 'timestamp'> => ({
  source,
  userId,
  tenantId,
  data,
  metadata
});